import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { authedStreamUrl, getBook, getBookEvents } from "../api/client";
import type { Book, BookStatus, EventRow } from "../api/types";
import { isTerminalStatus } from "../lib/stage";
import { queryKeys } from "./queryKeys";

/**
 * Live updates for a single book (§7 "Live updates").
 *
 * - Fetches the existing event log once on mount.
 * - While the book is queued/processing, opens an SSE connection: `update` frames are written
 *   straight into the `["book", id]` query cache (shared with useBook), `log` frames are
 *   appended to the local event list.
 * - If the stream errors or closes while still non-terminal, falls back to 3s polling of
 *   GET /api/books/{id}.
 * - Everything stops once status is terminal.
 */
export function useBookStream(id: string | undefined, status: BookStatus | null | undefined) {
  const queryClient = useQueryClient();
  const [events, setEvents] = useState<EventRow[]>([]);
  const [isLive, setIsLive] = useState(false);
  const lastEventIdRef = useRef(0);

  // Initial event log fetch (once per book).
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setEvents([]);
    lastEventIdRef.current = 0;
    getBookEvents(id)
      .then((res) => {
        if (cancelled) return;
        setEvents(res.events);
        if (res.events.length > 0) {
          lastEventIdRef.current = res.events[res.events.length - 1].id;
        }
      })
      .catch(() => {
        // Non-fatal — the live stream / polling below still drives the UI.
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const active = !!id && !!status && !isTerminalStatus(status);

  useEffect(() => {
    if (!id || !active) {
      setIsLive(false);
      return;
    }

    let stopped = false;
    let pollHandle: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      if (pollHandle != null) {
        clearInterval(pollHandle);
        pollHandle = null;
      }
    };

    const startPolling = () => {
      if (pollHandle != null || stopped) return;
      setIsLive(false);
      pollHandle = setInterval(() => {
        getBook(id)
          .then((book) => {
            if (stopped) return;
            queryClient.setQueryData(queryKeys.book(id), book);
            if (isTerminalStatus(book.status)) {
              stopPolling();
            }
          })
          .catch(() => {
            // transient — keep polling
          });
      }, 3000);
    };

    const attach = (src: EventSource) => {
      src.addEventListener("open", () => {
        if (!stopped) setIsLive(true);
      });

      src.addEventListener("update", (evt) => {
        try {
          const book = JSON.parse((evt as MessageEvent<string>).data) as Book;
          queryClient.setQueryData(queryKeys.book(id), book);
        } catch {
          // ignore malformed frame
        }
      });

      src.addEventListener("log", (evt) => {
        try {
          const row = JSON.parse((evt as MessageEvent<string>).data) as EventRow;
          if (row.id > lastEventIdRef.current) {
            lastEventIdRef.current = row.id;
            setEvents((prev) => [...prev, row]);
          }
        } catch {
          // ignore malformed frame
        }
      });

      src.onerror = () => {
        if (stopped) return;
        src.close();
        startPolling();
      };
    };

    // The URL may carry a Firebase ID token, which is an async lookup.
    let source: EventSource | null = null;
    void authedStreamUrl(id).then((url) => {
      if (stopped) return;
      source = new EventSource(url);
      attach(source);
    });

    return () => {
      stopped = true;
      source?.close();
      stopPolling();
    };
  }, [id, active, queryClient]);

  return { events, isLive };
}
